import { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Checkbox, Modal, Radio } from 'antd'
import moment from 'moment'
import Image from 'components/image/Image'
import StatusTag from 'components/status-tag/StatusTag'
import { IPolicy } from 'types/interfaces'
import { DateFormat } from 'types/enums'
import { AttachmentIcon } from '../assets/svgs'
import '../styles/ai-agent.import-picker.less'

/** Each class option's consequence, in the user's terms. */
const CLASS_OUTCOME_TEXT = {
  add: 'Adds to your current comparison',
  open: 'Starts a new comparison',
  switch: 'Switches your cover selection',
  replace: 'Clears and replaces your comparison',
} as const

export interface ImportedPolicyPickerProps {
  open: boolean
  fileName: string
  /** Every policy the uploaded wording matched, in the order the import named them. */
  policies: IPolicy[]
  /** Columns the comparison has free for this import. */
  availableColumns: number
  /**
   * Matched policies that already hold a column. They cost no new column and are not
   * added but refreshed, so they are marked as such and left out of the column count.
   */
  policiesInComparison?: number[]
  /**
   * Set when the import moves the comparison to another class. It says so here rather than
   * in a second confirm on top of this one — the user is already being asked one question
   * about this upload. `clearsColumns` separates losing work from merely changing what is
   * on screen, which are different things to agree to.
   */
  classChange?: { from?: string; to?: string; clearsColumns: boolean }
  /**
   * The cover selection this import would leave behind, for the user to edit. In a
   * comparison that is what is already loaded plus anything the wording needs; on the
   * dashboard it is the wording's own covers. `inComparison` marks the ones already on
   * screen, so adding a cover reads differently from keeping one.
   */
  coverOptions?: { id: number; name: string; inComparison?: boolean }[]
  /** The class those covers belong to — a cover name means little without it. */
  coverClassName?: string
  /**
   * The classes the wording has covers in, the default first. A combined wording — PI and
   * Public Liability in one booklet — can be compared in either, and which one is the
   * user's call. With one class or none there is nothing to choose and nothing is shown.
   */
  classOptions?: {
    id: number | string
    name: string
    isCurrent: boolean
    /** What choosing it does to the comparison on screen. */
    outcome?: keyof typeof CLASS_OUTCOME_TEXT
  }[]
  selectedClassId?: number | string
  onClassChange?: (classId: number | string) => void
  onCancel: () => void
  onConfirm: (choice: { policies: IPolicy[]; coverIds: number[] }) => void
}

/**
 * What changing class means for what is on screen, in the user's terms.
 *
 * Losing a comparison and merely switching which class is loaded are different things to
 * agree to, and an empty comparison loses nothing — so it is told, not warned.
 */
export const classChangeMessage = (
  { from, to, clearsColumns }: { from?: string; to?: string; clearsColumns: boolean },
  policyCount = 2,
) => {
  const subject = policyCount === 1 ? 'This policy belongs' : 'These policies belong'
  const named = to ? ` (${to})` : ''

  if (clearsColumns) {
    return `${subject} to a different class${named}. Your current comparison will be cleared and replaced.`
  }
  if (from && to) {
    return `${subject} to a different class. Your cover selection will switch from ${from} to ${to}.`
  }
  return `${subject} to a different class${named}. Your cover selection will switch to it.`
}

/**
 * Lets the user say which of an uploaded wording's policies to compare.
 *
 * One PDF can match several policy records — the same booklet published as separate
 * products, or under more than one brand — and they are rarely all wanted at once. The
 * comparison also holds a fixed number of columns, so past a point the choice has to be
 * made by someone; asking is better than silently taking the first few.
 */
function ImportedPolicyPicker({
  open,
  fileName,
  policies,
  availableColumns,
  policiesInComparison,
  classChange,
  coverOptions,
  coverClassName,
  classOptions,
  selectedClassId,
  onClassChange,
  onCancel,
  onConfirm,
}: ImportedPolicyPickerProps) {
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [selectedCoverIds, setSelectedCoverIds] = useState<number[]>([])

  const alreadyIn = useMemo(() => new Set(policiesInComparison ?? []), [policiesInComparison])

  /** Only a policy that is not on screen yet costs a column; the rest are refreshed. */
  const columnsUsedBy = (ids: number[]) => ids.filter((id) => !alreadyIn.has(id)).length

  // Everything that fits starts selected: the user asked for this document, so the default
  // is to take what it matched, across the covers it is written against. Re-run per opening
  // so a second upload never inherits the previous one's choice.
  useEffect(() => {
    if (!open) return
    let room = availableColumns
    const initial: number[] = []
    policies.forEach((policy) => {
      if (alreadyIn.has(policy.id)) {
        initial.push(policy.id)
        return
      }
      if (room > 0) {
        initial.push(policy.id)
        room -= 1
      }
    })
    setSelectedIds(initial)
    setSelectedCoverIds((coverOptions ?? []).map((cover) => cover.id))
  }, [open, policies, availableColumns, coverOptions, alreadyIn])

  const columnsUsed = columnsUsedBy(selectedIds)
  const atLimit = columnsUsed >= availableColumns

  const toggle = (policy: IPolicy) => {
    setSelectedIds((current) => {
      if (current.includes(policy.id)) return current.filter((id) => id !== policy.id)
      // A policy already on screen is going back into the column it holds, so the limit
      // has nothing to say about it.
      if (!alreadyIn.has(policy.id) && columnsUsedBy(current) >= availableColumns) return current
      return [...current, policy.id]
    })
  }

  const toggleCover = (coverId: number) => {
    setSelectedCoverIds((current) =>
      current.includes(coverId) ? current.filter((id) => id !== coverId) : [...current, coverId],
    )
  }

  const selected = useMemo(
    () => policies.filter((policy) => selectedIds.includes(policy.id)),
    [policies, selectedIds],
  )

  // Only the policies that need a column of their own can fail to fit.
  const tooManyToFit = columnsUsedBy(policies.map((policy) => policy.id)) > availableColumns
  const offersPolicyChoice = policies.length > 1
  const offersCovers = (coverOptions?.length ?? 0) > 1
  const offersClasses = (classOptions?.length ?? 0) > 1 && !!onClassChange
  /**
   * With one policy and one cover there is nothing to choose between, and the dialog is a
   * confirmation instead: the match and the cover are shown rather than offered. It still
   * opens, because an upload changes the table and the user should see what was matched
   * before that happens rather than after.
   */
  const soleCover = offersCovers ? undefined : coverOptions?.[0]
  // Marking what is new only means something when the comparison already holds covers.
  const showsWhichAreNew =
    (coverOptions?.some((cover) => cover.inComparison) ?? false) &&
    (coverOptions?.some((cover) => !cover.inComparison) ?? false)
  // A comparison with no covers has no rows, so there is nothing to compare against.
  const canConfirm = selectedIds.length > 0 && (!offersCovers || selectedCoverIds.length > 0)

  const title = () => {
    if (offersPolicyChoice) return 'What would you like to compare?'
    if (offersClasses) return 'How would you like to compare this policy?'
    if (offersCovers) return 'Which covers would you like?'
    return 'Add this policy to your comparison?'
  }

  return (
    <Modal
      className='ai-agent-import-picker'
      open={open}
      onCancel={onCancel}
      closable={false}
      // An import changes the table, and a stray click on the backdrop is not a decision
      // about it. Leaving on purpose means CANCEL, or Esc.
      maskClosable={false}
      centered
      destroyOnClose
      width={640}
      title={title()}
      footer={
        <>
          <Button onClick={onCancel} aria-label='Cancel this import'>
            CANCEL
          </Button>
          <Button
            type='primary'
            disabled={!canConfirm}
            onClick={() => onConfirm({ policies: selected, coverIds: selectedCoverIds })}
            aria-label='Add the selected policies to the comparison'
          >
            ADD TO COMPARISON
          </Button>
        </>
      }
    >
      <div className='ai-agent-import-picker-file'>
        <AttachmentIcon aria-hidden='true' />
        <span className='ai-agent-import-picker-file-name'>{fileName}</span>
        <span className='ai-agent-import-picker-file-match'>
          {policies.length === 1 ? '1 match' : `${policies.length} matches`}
        </span>
      </div>

      {/* Asked before the warning below, because the answer is what decides the warning:
          the class the user is in adds to the comparison, another one replaces it. */}
      {offersClasses && (
        <section className='ai-agent-import-picker-classes'>
          <h4 className='ai-agent-import-picker-heading'>
            Compare as
            <span className='ai-agent-import-picker-heading-note'>
              This wording covers {classOptions?.length} classes
            </span>
          </h4>
          <Radio.Group
            className='ai-agent-import-picker-class-list'
            value={selectedClassId}
            onChange={(event) => onClassChange?.(event.target.value)}
          >
            {classOptions?.map((option) => {
              const selected = option.id === selectedClassId
              return (
                <Radio
                  key={option.id}
                  value={option.id}
                  className={`ai-agent-import-picker-class${selected ? ' is-selected' : ''}`}
                >
                  <span className='ai-agent-import-picker-class-name'>
                    {option.name}
                    {option.isCurrent && (
                      <span className='ai-agent-import-picker-row-tag'>current</span>
                    )}
                  </span>
                  {/* Starting a new comparison costs nothing, so it is not worth a line. */}
                  {option.outcome && option.outcome !== 'open' && (
                    <span className={`ai-agent-import-picker-class-outcome is-${option.outcome}`}>
                      {CLASS_OUTCOME_TEXT[option.outcome]}
                    </span>
                  )}
                </Radio>
              )
            })}
          </Radio.Group>
        </section>
      )}

      {classChange && (
        <Alert
          type={classChange.clearsColumns ? 'warning' : 'info'}
          showIcon
          className='ai-agent-import-picker-alert'
          message={classChangeMessage(classChange, policies.length)}
        />
      )}

      {tooManyToFit && (
        <Alert
          type='info'
          showIcon
          className='ai-agent-import-picker-alert'
          message={`A comparison holds ${availableColumns} more ${
            availableColumns === 1 ? 'policy' : 'policies'
          }, so not all of these will fit. Choose the ones you want.`}
        />
      )}

      <section className='ai-agent-import-picker-panel'>
        {offersPolicyChoice && (
          <h4 className='ai-agent-import-picker-heading'>
            Policies
            <span className='ai-agent-import-picker-heading-note'>
              {columnsUsed} of {availableColumns} column
              {availableColumns === 1 ? '' : 's'} used
            </span>
          </h4>
        )}

        <ul className='ai-agent-import-picker-list'>
          {policies.map((policy) => {
            const checked = selectedIds.includes(policy.id)
            const isAlreadyIn = alreadyIn.has(policy.id)
            // A row that cannot be ticked still has to be readable — it is one of the
            // matches, and the user needs to see what they are leaving out. One already on
            // screen is never blocked: it is going back into the column it holds.
            const disabled = !checked && atLimit && !isAlreadyIn
            return (
              <li key={policy.id}>
                <label
                  className={`ai-agent-import-picker-row${checked ? ' is-selected' : ''}${
                    disabled ? ' is-disabled' : ''
                  }`}
                >
                  {/* A lone match is what the dialog is confirming, not something to pick
                    between, so it is shown rather than offered. */}
                  {offersPolicyChoice && (
                    <Checkbox
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggle(policy)}
                    />
                  )}
                  {policy.insurer?.logo && (
                    <Image
                      className='ai-agent-import-picker-logo'
                      src={policy.insurer.logo}
                      alt={policy.insurer?.name}
                    />
                  )}
                  <span className='ai-agent-import-picker-detail'>
                    <span className='ai-agent-import-picker-name'>
                      {policy.name}
                      {/* Ticking this one costs no column and adds no second copy — it
                        refreshes the wording in the column it is already in. */}
                      {isAlreadyIn && (
                        <span className='ai-agent-import-picker-row-tag'>in your comparison</span>
                      )}
                    </span>
                    <span className='ai-agent-import-picker-meta'>
                      {policy.insurer?.name}
                      {policy.code ? ` · ${policy.code}` : ''}
                      {policy.start_date
                        ? ` · ${moment(policy.start_date).format(DateFormat.AU)}`
                        : ''}
                      {isAlreadyIn && checked ? ' · its wording will be refreshed' : ''}
                    </span>
                  </span>
                  <StatusTag policy={policy} />
                </label>
              </li>
            )
          })}
        </ul>

        {/* One cover is not a choice, but it is still worth naming what will be loaded. */}
        {soleCover && (
          <p className='ai-agent-import-picker-sole-cover'>
            Cover
            <span>
              {coverClassName ? `${coverClassName} · ` : ''}
              {soleCover.name}
            </span>
          </p>
        )}
      </section>

      {/*
        The covers become the comparison's rows. A wording written against six of them
        opens a table most of which the user did not ask to see, and one arriving into a
        comparison can widen the selection — either way this is the list the table will be
        built from, so it is shown and left editable.
      */}
      {offersCovers && (
        <section className='ai-agent-import-picker-panel'>
          <h4 className='ai-agent-import-picker-heading'>
            Covers to compare
            {coverClassName && (
              <span className='ai-agent-import-picker-heading-note'>{coverClassName}</span>
            )}
          </h4>
          <ul className='ai-agent-import-picker-covers'>
            {coverOptions?.map((cover) => {
              const checked = selectedCoverIds.includes(cover.id)
              return (
                <li key={cover.id}>
                  <label className={`ai-agent-import-picker-cover${checked ? ' is-selected' : ''}`}>
                    <Checkbox checked={checked} onChange={() => toggleCover(cover.id)} />
                    <span>{cover.name}</span>
                    {/* Only worth saying where some covers are already loaded and some are
                        not — on a fresh comparison every one of them is new. */}
                    {showsWhichAreNew && !cover.inComparison && (
                      <span className='ai-agent-import-picker-cover-tag'>new</span>
                    )}
                  </label>
                </li>
              )
            })}
          </ul>
          {!selectedCoverIds.length && (
            <p className='ai-agent-import-picker-error'>Choose at least one cover to compare.</p>
          )}
        </section>
      )}
    </Modal>
  )
}

export { ImportedPolicyPicker }
